import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteEvent as deleteEventApi,
  getEventById,
  signOutFromEvent,
  signUpForEvent,
  updateEvent,
  signUpForWorkingWeekend,
} from "../../api/events";
import { EventDto, WWRegistrationDto } from "../../types/eventTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { AxiosError } from "axios";
import { authReducerActionType } from "../../types/globalTypes";
import { useNavigate } from "react-router-dom";

/**
 * Hook to get a specific event by ID
 * @param eventId The ID of the event to get
 */
export const useEventDetails = (eventId: number) => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // ----------------------------------------------------------------------------------
  // ############
  // GET QUERIES
  // ############

  // getEventById query
  const {
    data: eventByIdData,
    isLoading: isEventByIdLoading,
    isError: isEventByIdError,
  } = useQuery({
    queryKey: ["eventDetails", eventId],
    queryFn: () => getEventById(eventId),
    enabled: !!eventId, // enable the query only if eventId is provided
  });

  const eventDetails = eventByIdData?.data;

  // ----------------------------------------------------------------------------------
  // ----------------------------------------------------------------------------------
  // ############
  // UPDATE QUERIES
  // ############

  // update event mutation
  const { mutateAsync: updateEventMutation } = useMutation({
    mutationFn: (event: EventDto) => updateEvent(event),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["eventDetails", eventId] });
      showSuccessMessage("Event erfolgreich aktualisiert");
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Event aktualisieren fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Function to update the event details, calls the updateEvent mutation
   * @param event The event to update, must contain the ID of the event to update
   */
  const updateEventDetails = async (event: EventDto) => {
    await updateEventMutation(event);
  };

  // signUpForEvent mutation
  const { mutateAsync: signUpForEventMutatation } = useMutation({
    mutationFn: ({ eventId, participantMemberId }: { eventId: number; participantMemberId: number }) =>
      signUpForEvent(eventId, participantMemberId),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich für Event angemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails", eventId] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Anmeldung für Event fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Function to sign the user up for the event, calls the signUpForEvent mutation
   * @param participantMemberId The ID of the member to sign up for the event
   */
  const signUserUpForEvent = async (participantMemberId: number) => {
    await signUpForEventMutatation({
      eventId: eventId,
      participantMemberId: participantMemberId,
    });
  };

  const { mutateAsync: signUpForWorkingWeekendMutation } = useMutation({
    mutationFn: ({
      eventId,
      memberId,
      wwRegistration,
    }: {
      eventId: number;
      memberId: number;
      wwRegistration: WWRegistrationDto;
    }) => signUpForWorkingWeekend(eventId, memberId, wwRegistration),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich für Working Weekend angemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails", eventId] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Anmeldung für Working Weekend fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Function to sign the user up for the working weekend, calls the signUpForWorkingWeekend mutation
   * @param eventId The ID of the event for which to sign up
   * @param memberId The ID of the member to sign up for the working weekend
   * @param wwRegistration The information for the working weekend registration
   */
  const signUserUpForWorkingWeekend = async (eventId: number, memberId: number, wwRegistration: WWRegistrationDto) => {
    await signUpForWorkingWeekendMutation({
      eventId: eventId,
      memberId: memberId,
      wwRegistration: wwRegistration,
    });
  };

  // ----------------------------------------------------------------------------------
  // ############
  // DELETE QUERIES
  // ############

  // signOutFromEvent mutation
  const { mutateAsync: signOutFromEventMutation } = useMutation({
    mutationFn: ({ eventId, participantMemberId }: { eventId: number; participantMemberId: number }) =>
      signOutFromEvent(eventId, participantMemberId),
    onSuccess: () => {
      showSuccessMessage("Erfolgreich von Event abgemeldet");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails", eventId] });
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Abmeldung von Event fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Function to sign the user out from the event, calls the signOutFromEvent mutation
   * @param participantMemberId The ID of the member to sign out from the event
   */
  const signUserOutFromEvent = async (participantMemberId: number) => {
    await signOutFromEventMutation({
      eventId: eventId,
      participantMemberId: participantMemberId,
    });
  };

  // delete event mutation
  const { mutateAsync: deleteEventMutation } = useMutation({
    mutationFn: deleteEventApi,
    onSuccess: () => {
      showSuccessMessage("Event erfolgreich gelöscht");
      // Invalidate and refetch events and userEvents queries to get the updated data
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["userEvents"] });
      queryClient.invalidateQueries({ queryKey: ["eventDetails", eventId] });
      // Redirect to events overview after successful deletion
      navigate(`/veranstaltungen`);
    },
    onError: (err: AxiosError) => {
      showErrorMessage("Event löschen fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
  });

  /**
   * Function to delete the event, calls the deleteEvent mutation
   * @param eventId The ID of the event to delete
   */
  const deleteEvent = async () => {
    await deleteEventMutation(eventId);
  };

  return {
    eventDetails,
    isEventByIdLoading,
    isEventByIdError,
    updateEventDetails,
    signUserUpForEvent,
    signUserOutFromEvent,
    deleteEvent,
    signUserUpForWorkingWeekend,
  };
};
